import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '@components/button/Button';
import CalendarInput from '@components/calendar-input/CalendarInput';
import Dropdown from '@components/drop-down/Dropdown';
import TextArea from '@components/text-area/TextArea';
import TimePicker from '@components/time-picker/TimePicker';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import {
  INPUT_BASE_CLASSNAME,
  INPUT_FOCUS_CLASSNAME
} from '@features/lead-management/constants/common';
import useNotify from '@hooks/notify';
import {
  appendTimeToISODate,
  formatISODate,
  getCurrentISODateString,
  getCurrentTimeString
} from '@utils/date';
import { getLocalStorageItem } from '@utils/storage';

import { CallTypeOptions, Outcome } from '../../constants/leadActivity';
import { useCreateLeadCallMutation } from '../../queries/LeadManagementQuery';
import {
  CallFormData,
  CallModalProps,
  CallOutcome,
  CallType
} from '../../types/leadActivity';

const AddCallModal: React.FC<CallModalProps> = ({
  onClose,
  leadId,
  initialData = {},
  analyticsData
}) => {
  const organisationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { currentOrg } = useSelector((state: any) => state.user);
  const { leadDetails } = analyticsData ?? {};

  const { createErrorAlert, createSuccessAlert } = useNotify();

  const [createLeadCall, { isLoading }] = useCreateLeadCallMutation();

  const [formData, setFormData] = useState<CallFormData>({
    call_type: CallType.INBOUND,
    date: getCurrentISODateString(),
    time: getCurrentTimeString(),
    outcome: CallOutcome.INTERESTED,
    remarks: '',
    ...initialData
  });

  const isEditing = Boolean(initialData.id);

  const handleInputChange = useCallback(
    (field: keyof CallFormData, value: any) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }));
    },
    []
  );

  const isFormValid = useCallback(
    () =>
      !!(
        formData.call_type &&
        formData.date &&
        formData.time &&
        formData.outcome
      ),
    [formData]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const timestamp = appendTimeToISODate(formData.date, formData.time);

      const callTypeEnum = (
        formData.call_type === CallType.INBOUND
          ? CallType.INBOUND
          : CallType.OUTBOUND
      ) as CallType;
      const outcomeEnum = formData.outcome as CallOutcome;

      const submitData = {
        id: formData.id,
        call_type: callTypeEnum,
        timestamp,
        outcome: outcomeEnum,
        remarks: formData.remarks ? formData.remarks : null
      };

      const response = await createLeadCall({
        leadId,
        organizationId: organisationId,
        call: submitData
      }).unwrap();

      if (!('errors' in response)) {
        sendMixpanelEvent(
          isEditing
            ? Event.LEAD_ACTIVITIES_CALL_INFO_UPDATED
            : Event.LEAD_ACTIVITIES_CALL_INFO_ADDED,
          {
            org_id: organisationId,
            org_name: currentOrg?.name,
            lead_id: leadId,
            lead_name: leadDetails?.leadName,
            lead_phone: leadDetails?.leadPhone,
            call_info_id: response?.id || formData.id,
            scheduled_datetime: timestamp,
            call_type: formData.call_type
          }
        );
        createSuccessAlert(
          `Call ${isEditing ? 'edited' : 'created'} successfully`
        );
        onClose();
      } else {
        createErrorAlert(`Failed to ${isEditing ? 'update' : 'create'} call`);
        onClose();
      }
    } catch (submitError) {
      createErrorAlert(`Failed to ${isEditing ? 'update' : 'create'} call`);
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData,
    createLeadCall,
    onClose,
    leadId,
    leadDetails?.leadName,
    leadDetails?.leadPhone,
    createErrorAlert,
    createSuccessAlert,
    organisationId,
    isEditing
  ]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <>
      <div className="space-y-2 px-6 pb-1">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Call Type <span className="text-red-500">*</span>
          </label>
          <Dropdown
            filterOptions={CallTypeOptions}
            currentFilter={formData.call_type}
            onSelectFilter={(value: string) =>
              handleInputChange('call_type', value)
            }
            placeholder="Select Call Type"
            className={`w-full ${INPUT_FOCUS_CLASSNAME}`}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Date <span className="text-red-500">*</span>
            </label>
            <div className="flex h-12.5 items-center">
              <CalendarInput
                selectedDate={
                  formData.date ? new Date(formData.date) : undefined
                }
                handleDateChange={(date?: Date) =>
                  handleInputChange(
                    'date',
                    date
                      ? formatISODate({
                          date,
                          separator: '-',
                          format: 'yyyy-mm-dd'
                        })
                      : ''
                  )
                }
                placeholder="Select Date"
                className="w-full [&_input]:!flex [&_input]:!h-12.5 [&_input]:!items-center"
                inputStyle={INPUT_BASE_CLASSNAME}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Time <span className="text-red-500">*</span>
            </label>
            <TimePicker
              label=""
              time={formData.time}
              handleChange={(value: string) => handleInputChange('time', value)}
              defaultToCurrentTime
              labelStyle="hidden"
              className="w-full"
              popupClassName="!fixed !top-1/2 !left-1/2 !transform !-translate-x-1/2 !-translate-y-1/2 !z-[9999]"
              pickerInputStyles={`${INPUT_BASE_CLASSNAME} ${INPUT_FOCUS_CLASSNAME}`}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Outcome <span className="text-red-500">*</span>
          </label>
          <div className="flex h-12.5 items-center">
            <Dropdown
              filterOptions={Outcome}
              currentFilter={formData.outcome}
              onSelectFilter={(value: string) =>
                handleInputChange('outcome', value)
              }
              placeholder="Select an Outcome"
              className={`w-full ${INPUT_FOCUS_CLASSNAME}`}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">Remarks</label>
          <TextArea
            value={formData.remarks}
            onChange={(value: string) => handleInputChange('remarks', value)}
            placeholder="Add a note"
            rows={3}
            className="w-full"
            inputStyle={INPUT_BASE_CLASSNAME}
          />
        </div>
      </div>

      <div className="flex gap-4 rounded-b-xl border-t border-gray-200 p-6">
        <Button
          variant="secondary"
          handleClick={handleClose}
          label="Cancel"
          className="flex-1"
        />
        <Button
          handleClick={handleSubmit}
          label="Done"
          className="flex-1"
          loading={isLoading}
          disabled={!isFormValid()}
        />
      </div>
    </>
  );
};

export default AddCallModal;
